import { CreateOrganizationDialog } from "@/components/create-organization-dialog";
import { DeleteOrganizationDialog } from "@/components/delete-organization-dialog";
import { RenameOrganizationDialog } from "@/components/rename-organization-dialog";
import { ServiceAccountDialog, useServiceAccountStatus } from "@/components/service-account-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useMyOrganizations } from "@/hooks/use-users";
import { useOrgStore } from "@/stores";
import { AlertTriangle, Building2, KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import React from "react";

interface ManageOrganizationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ManageOrganizationsDialog({ open, onOpenChange, onSuccess }: ManageOrganizationsDialogProps) {
  const orgsQuery = useMyOrganizations();
  const organizations = orgsQuery.data || [];
  const isLoading = orgsQuery.isLoading;
  const fetchUserOrgs = useOrgStore.use.fetchUserOrgs();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [createOrgOpen, setCreateOrgOpen] = React.useState(false);
  const [deleteOrgOpen, setDeleteOrgOpen] = React.useState(false);
  const [orgToDelete, setOrgToDelete] = React.useState<{ id: string; name: string } | null>(null);
  const [renameOrgOpen, setRenameOrgOpen] = React.useState(false);
  const [orgToRename, setOrgToRename] = React.useState<{ id: string; name: string } | null>(null);
  const [saDialogOpen, setSaDialogOpen] = React.useState(false);
  const [saOrg, setSaOrg] = React.useState<{ id: string; name: string } | null>(null);

  // Child dialogs render as siblings of this one, so they must follow it closed.
  React.useEffect(() => {
    if (open) return;
    setRenameOrgOpen(false);
    setOrgToRename(null);
    setDeleteOrgOpen(false);
    setOrgToDelete(null);
    setSaDialogOpen(false);
    setSaOrg(null);
  }, [open]);

  const handleOrgCreated = () => {
    orgsQuery.refetch();
    fetchUserOrgs();
    onSuccess?.();
  };

  const handleOrgDeleted = () => {
    orgsQuery.refetch();
    fetchUserOrgs();
    setOrgToDelete(null);
    onSuccess?.();
  };

  const handleOrgRenamed = () => {
    orgsQuery.refetch();
    fetchUserOrgs();
    setOrgToRename(null);
    onSuccess?.();
  };

  const handleRenameClick = (org: { id: string; name: string }) => {
    setOrgToRename(org);
    setRenameOrgOpen(true);
  };

  const handleDeleteClick = (org: { id: string; name: string }) => {
    setOrgToDelete(org);
    setDeleteOrgOpen(true);
  };

  const handleSaClick = (org: { id: string; name: string }) => {
    setSaOrg(org);
    setSaDialogOpen(true);
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Manage Organizations</DialogTitle>
            <DialogDescription>Create, rename, or delete organizations</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
              <Button onClick={() => setCreateOrgOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading organizations...</div>
              </div>
            ) : filteredOrganizations.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">
                  {searchQuery ? "No organizations found" : "No organizations available"}
                </div>
              </div>
            ) : (
              <div className="max-h-[400px] space-y-2 overflow-y-auto">
                {filteredOrganizations.map((org) => (
                  <OrgRow
                    key={org.id}
                    org={org}
                    parentDialogOpen={open}
                    onRenameClick={handleRenameClick}
                    onDeleteClick={handleDeleteClick}
                    onSaClick={handleSaClick}
                  />
                ))}
              </div>
            )}

            <div className="rounded-md bg-muted p-3">
              <p className="text-xs text-muted-foreground">
                <strong>Note:</strong> Organizations can only be deleted if they have no active users or licenses.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CreateOrganizationDialog open={createOrgOpen} onOpenChange={setCreateOrgOpen} onSuccess={handleOrgCreated} />

      {orgToRename && (
        <RenameOrganizationDialog
          open={renameOrgOpen}
          onOpenChange={setRenameOrgOpen}
          organizationId={orgToRename.id}
          organizationName={orgToRename.name}
          onSuccess={handleOrgRenamed}
        />
      )}

      {orgToDelete && (
        <DeleteOrganizationDialog
          open={deleteOrgOpen}
          onOpenChange={setDeleteOrgOpen}
          organizationId={orgToDelete.id}
          organizationName={orgToDelete.name}
          onSuccess={handleOrgDeleted}
        />
      )}

      {saOrg && (
        <ServiceAccountDialog
          open={saDialogOpen}
          onOpenChange={setSaDialogOpen}
          organizationId={saOrg.id}
          organizationName={saOrg.name}
        />
      )}
    </>
  );
}

function OrgRow({
  org,
  parentDialogOpen,
  onRenameClick,
  onDeleteClick,
  onSaClick,
}: {
  org: { id: string; name: string };
  parentDialogOpen: boolean;
  onRenameClick: (org: { id: string; name: string }) => void;
  onDeleteClick: (org: { id: string; name: string }) => void;
  onSaClick: (org: { id: string; name: string }) => void;
}) {
  const saQuery = useServiceAccountStatus(org.id, parentDialogOpen);
  const hasSA = saQuery.data !== null && saQuery.data !== undefined;
  const isLoaded = !saQuery.isLoading;
  const jfrogStatus = saQuery.data?.jfrog_status;

  return (
    <div className="flex items-center justify-between rounded-md border p-3 hover:bg-muted">
      <div className="flex items-center gap-3">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{org.name}</span>
            {isLoaded && !hasSA && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="destructive" className="gap-1 px-1.5 py-0 text-[10px]">
                    <AlertTriangle className="h-3 w-3" />
                    No SA
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>No JFrog service account configured</TooltipContent>
              </Tooltip>
            )}
            {isLoaded && hasSA && jfrogStatus === "invalid" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="destructive" className="gap-1 px-1.5 py-0 text-[10px]">
                    <AlertTriangle className="h-3 w-3" />
                    Invalid SA
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>JFrog token has been revoked or deleted externally</TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{org.id}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onSaClick(org)}>
          <KeyRound className="mr-2 h-4 w-4" />
          Service Account
        </Button>
        <Button variant="outline" size="sm" onClick={() => onRenameClick(org)}>
          <Pencil className="mr-2 h-4 w-4" />
          Rename
        </Button>
        <Button variant="outline" size="sm" onClick={() => onDeleteClick(org)}>
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </Button>
      </div>
    </div>
  );
}
